import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { 
  FiPlus, 
  FiEdit,
  FiTrash2, 
  FiImage,
  FiDollarSign,
  FiPackage,
  FiAlertCircle,
  FiLoader,
  FiChevronLeft,
  FiChevronRight,
  FiZoomIn,
  FiRefreshCw,
  FiArchive,
  FiRotateCcw,
  FiX,
  FiChevronsLeft,
  FiChevronsRight,
  FiSearch,
  FiFilter,
  FiGrid,
  FiList,
  FiEye,
  FiEyeOff
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ASSET_BASE_URL = (process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || 'https://pcvasquez.com/api')
  : 'http://localhost:5000/api').replace(/\/api$/, '');

const ProductList = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [allDeletedProducts, setAllDeletedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [deletedProducts, setDeletedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showFullscreenImage, setShowFullscreenImage] = useState(false);
  const [currentProductImages, setCurrentProductImages] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  
  // Estados para paginación y UI
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('name'); // 'name', 'price', 'stock'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showQuickView, setShowQuickView] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchAllProducts();
  }, [showDeleted]);

  useEffect(() => {
    calculatePagination();
  }, [allProducts, allDeletedProducts, currentPage, itemsPerPage, showDeleted, searchTerm, sortBy, sortOrder]);

  const fetchAllProducts = async () => {
    try {
      setLoading(true);
      let rawProducts = [];

      if (showDeleted) {
        const response = await api.get('/products/deleted');
        if (!response.data?.success) throw new Error('Formato de productos archivados inesperado');
        rawProducts = response.data.data || [];
      } else {
        // La API pública limita cada respuesta a 48 productos. El administrador
        // reúne todas las páginas para que su paginación, búsqueda y filtros
        // trabajen sobre el catálogo completo, no solo sobre los primeros 12.
        const limit = 48;
        let page = 1;
        let pages = 1;

        do {
          const response = await api.get('/products', { params: { page, limit } });
          if (!response.data?.success || !Array.isArray(response.data.data)) {
            throw new Error('Formato de productos inesperado');
          }
          rawProducts.push(...response.data.data);
          pages = Number(response.data.pagination?.pages) || 1;
          page += 1;
        } while (page <= pages);
      }

      const processedProducts = rawProducts.map(product => ({
          ...product,
          allImages: [
            product.image ? getImageUrl(product.image) : '/placeholder.jpg',
            ...((product.images || product.additionalImages || [])
              .filter(image => image && image !== product.image)
              .map(image => getImageUrl(image)))
          ]
      }));

      if (showDeleted) {
        setAllDeletedProducts(processedProducts);
      } else {
        setAllProducts(processedProducts);
      }
      setTotalItems(processedProducts.length);
      setError(null);
    } catch (err) {
      console.error('Error al obtener productos:', err);
      setError('Hubo un error al cargar los productos. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const calculatePagination = () => {
    let sourceArray = showDeleted ? [...allDeletedProducts] : [...allProducts];
    
    // Aplicar búsqueda
    if (searchTerm) {
      sourceArray = sourceArray.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Aplicar ordenamiento
    sourceArray.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'price':
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case 'stock':
          aValue = a.stock || 0;
          bValue = b.stock || 0;
          break;
        case 'name':
        default:
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
      }
      
      if (sortOrder === 'desc') {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    });

    const totalItemsCount = sourceArray.length;
    const calculatedTotalPages = Math.ceil(totalItemsCount / itemsPerPage);
    setTotalPages(calculatedTotalPages);
    
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(calculatedTotalPages);
      return;
    }
    
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedProducts = sourceArray.slice(startIndex, endIndex);
    
    if (showDeleted) {
      setDeletedProducts(paginatedProducts);
    } else {
      setProducts(paginatedProducts);
    }
    
    setTotalItems(totalItemsCount);
  };

  const getImageUrl = (imageName) => {
    if (!imageName) return '/placeholder.jpg';
    if (imageName.startsWith('http')) return imageName;
    if (imageName.includes('uploads/')) {
      return `${ASSET_BASE_URL}${imageName.startsWith('/') ? imageName : `/${imageName}`}`;
    }
    return `${ASSET_BASE_URL}/uploads/products/${imageName}`;
  };

  const handleAddProduct = () => {
    navigate('/admin/products/new');
  };

  const handleEditProduct = (id) => {
    navigate(`/admin/products/edit/${id}`);
  };

  const handleSoftDelete = async (id) => {
    if (!window.confirm('¿Está seguro que desea archivar este producto? Podrá restaurarlo más tarde.')) return;
    
    try {
      await api.patch(`/products/${id}/soft-delete`);
      setAllProducts(previousProducts => previousProducts.filter(product => product._id !== id));
      toast.success('Producto archivado correctamente', {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeButton: false,
        className: "bg-white border-l-4 border-[#662D8F] shadow-lg"
      });
    } catch (err) {
      console.error('Error al archivar producto:', err);
      toast.error('No se pudo archivar el producto', {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: true,
        className: "bg-white border-l-4 border-red-500 shadow-lg"
      });
    }
  };

  const handleRestoreProduct = async (id) => {
    try {
      await api.patch(`/products/${id}/restore`);
      setAllDeletedProducts(previousProducts => previousProducts.filter(product => product._id !== id));
      // Muestra de inmediato el catálogo activo y fuerza una consulta completa.
      setCurrentPage(1);
      setShowDeleted(false);
      toast.success('Producto restaurado correctamente', {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeButton: false,
        className: "bg-white border-l-4 border-green-500 shadow-lg"
      });
    } catch (err) {
      console.error('Error al restaurar producto:', err);
      toast.error('No se pudo restaurar el producto', {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: true,
        className: "bg-white border-l-4 border-red-500 shadow-lg"
      });
    }
  };

  const handleHardDelete = async (id) => {
    if (!window.confirm('¿Está seguro que desea eliminar permanentemente este producto? Esta acción no se puede deshacer.')) return;
    
    try {
      await api.delete(`/products/${id}`);
      setAllDeletedProducts(previousProducts => previousProducts.filter(product => product._id !== id));
      toast.success('Producto eliminado permanentemente', {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeButton: false,
        className: "bg-white border-l-4 border-red-500 shadow-lg"
      });
    } catch (err) {
      console.error('Error al eliminar producto:', err);
      toast.error('No se pudo eliminar el producto', {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: true,
        className: "bg-white border-l-4 border-red-500 shadow-lg"
      });
    }
  };

  const openImageGallery = (images, index = 0) => {
    setCurrentProductImages(images);
    setSelectedImageIndex(index);
    setShowFullscreenImage(true);
  };

  const openQuickView = (product) => {
    setSelectedProduct(product);
    setShowQuickView(true);
  };

  const nextImage = () => {
    setSelectedImageIndex((prev) => 
      (prev + 1) % currentProductImages.length
    );
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => 
      (prev - 1 + currentProductImages.length) % currentProductImages.length
    );
  };

  // Funciones de paginación
  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToFirstPage = () => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToLastPage = () => {
    setCurrentPage(totalPages);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleToggleDeleted = () => {
    setShowDeleted(!showDeleted);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-[#0C4B45]/5 via-white to-[#662D8F]/5 p-4 md:p-6">
        <motion.div
          animate={{ rotate: 360, scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="text-[#0C4B45] mb-4"
        >
          <div className="relative">
            <FiLoader size={50} className="md:w-14 md:h-14" />
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 text-[#662D8F] opacity-70"
            >
              <FiPackage size={30} className="absolute top-2 left-2" />
            </motion.div>
          </div>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-4 text-lg md:text-xl font-semibold text-[#0C4B45] bg-white/80 px-6 py-3 rounded-2xl shadow-lg"
        >
          Cargando catálogo...
        </motion.p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-[#0C4B45]/5 via-white to-[#662D8F]/5 p-4 md:p-6">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="bg-white p-8 rounded-3xl shadow-2xl max-w-md w-full text-center border border-gray-100"
        >
          <motion.div
            animate={{ shake: { rotate: [-5, 5, -5, 5, 0] } }}
            transition={{ duration: 0.5 }}
          >
            <FiAlertCircle size={60} className="mx-auto text-red-400 mb-4" />
          </motion.div>
          <h2 className="text-2xl font-bold text-[#0C4B45] mb-3">Error al Cargar</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">{error}</p>
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(12, 75, 69, 0.3)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.location.reload()}
            className="px-8 py-3 bg-gradient-to-r from-[#0C4B45] to-[#083D38] text-white rounded-2xl hover:from-[#083D38] hover:to-[#0C4B45] transition-all shadow-lg font-semibold"
          >
            Reintentar
          </motion.button>
        </motion.div>
      </div>
    );
  }

  const currentProducts = showDeleted ? deletedProducts : products;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0C4B45]/5 via-white to-[#662D8F]/5 p-4 md:p-6">
      <div className="w-full max-w-[1600px] mx-auto">
        {/* Header Mejorado */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 md:p-8 mb-8 border border-white/20"
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 bg-gradient-to-r from-[#0C4B45] to-[#083D38] rounded-2xl shadow-lg">
                  <FiPackage className="text-white text-xl" />
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#0C4B45]">
                    {showDeleted ? 'Productos Archivados' : 'Gestión de Productos'}
                  </h1>
                  <p className="text-gray-600 text-sm md:text-base mt-1">
                    {showDeleted 
                      ? 'Productos que han sido archivados temporalmente' 
                      : 'Administra y organiza tu catálogo de productos'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <motion.button
                whileHover={{ scale: 1.02, boxShadow: "0 8px 20px -5px rgba(0, 0, 0, 0.2)" }}
                whileTap={{ scale: 0.98 }}
                onClick={handleToggleDeleted}
                className={`flex items-center justify-center px-6 py-3 rounded-2xl transition-all shadow-lg font-semibold ${
                  showDeleted
                    ? 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-600'
                    : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-700 hover:to-gray-600'
                }`}
              >
                {showDeleted ? (
                  <>
                    <FiEye className="mr-2" size={18} />
                    Ver Activos
                  </>
                ) : (
                  <>
                    <FiEyeOff className="mr-2" size={18} />
                    Ver Archivados
                  </>
                )}
              </motion.button>
              
              {!showDeleted && (
                <motion.button
                  whileHover={{ scale: 1.02, boxShadow: "0 8px 25px -5px rgba(102, 45, 143, 0.4)" }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAddProduct}
                  className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#662D8F] to-[#512577] text-white rounded-2xl hover:from-[#512577] hover:to-[#662D8F] transition-all shadow-lg font-semibold"
                >
                  <FiPlus className="mr-2" size={18} />
                  Nuevo Producto
                </motion.button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Barra de Herramientas Mejorada */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-8 border border-white/20"
        >
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Búsqueda */}
            <div className="flex-1 w-full lg:max-w-md">
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Buscar productos..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0C4B45]/20 focus:border-[#0C4B45] transition-all shadow-sm"
                />
              </div>
            </div>

            {/* Controles de Vista y Ordenamiento */}
            <div className="flex flex-wrap gap-3 items-center">
              {/* Selector de Vista */}
              <div className="flex bg-gray-100 rounded-2xl p-1 shadow-inner">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-xl transition-all ${
                    viewMode === 'grid' 
                      ? 'bg-white text-[#0C4B45] shadow-md' 
                      : 'text-gray-500 hover:text-[#0C4B45]'
                  }`}
                >
                  <FiGrid size={18} />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-xl transition-all ${
                    viewMode === 'list' 
                      ? 'bg-white text-[#0C4B45] shadow-md' 
                      : 'text-gray-500 hover:text-[#0C4B45]'
                  }`}
                >
                  <FiList size={18} />
                </motion.button>
              </div>

              {/* Ordenamiento */}
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-4 py-2 shadow-sm">
                <FiFilter className="text-gray-400" size={16} />
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                    setCurrentPage(1);
                  }}
                  className="bg-transparent border-none focus:outline-none text-sm font-medium text-gray-700"
                >
                  <option value="name-asc">Nombre A-Z</option>
                  <option value="name-desc">Nombre Z-A</option>
                  <option value="price-asc">Precio: Menor a Mayor</option>
                  <option value="price-desc">Precio: Mayor a Menor</option>
                  <option value="stock-desc">Stock: Mayor a Menor</option>
                  <option value="stock-asc">Stock: Menor a Mayor</option>
                </select>
              </div>

              {/* Items por página */}
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-4 py-2 shadow-sm">
                <span className="text-sm text-gray-600 whitespace-nowrap">Mostrar:</span>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="bg-transparent border-none focus:outline-none text-sm font-medium text-gray-700"
                >
                  <option value={8}>8</option>
                  <option value={12}>12</option>
                  <option value={16}>16</option>
                  <option value={20}>20</option>
                  <option value={24}>24</option>
                </select>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Contadores y Estadísticas */}
        {totalItems > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Productos</p>
                  <p className="text-2xl font-bold text-[#0C4B45]">{totalItems}</p>
                </div>
                <div className="p-3 bg-[#0C4B45]/10 rounded-xl">
                  <FiPackage className="text-[#0C4B45]" size={20} />
                </div>
              </div>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Página Actual</p>
                  <p className="text-2xl font-bold text-[#662D8F]">{currentPage}</p>
                </div>
                <div className="p-3 bg-[#662D8F]/10 rounded-xl">
                  <FiList className="text-[#662D8F]" size={20} />
                </div>
              </div>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Productos Visibles</p>
                  <p className="text-2xl font-bold text-green-600">{currentProducts.length}</p>
                </div>
                <div className="p-3 bg-green-500/10 rounded-xl">
                  <FiEye className="text-green-500" size={20} />
                </div>
              </div>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Páginas</p>
                  <p className="text-2xl font-bold text-blue-600">{totalPages}</p>
                </div>
                <div className="p-3 bg-blue-500/10 rounded-xl">
                  <FiGrid className="text-blue-500" size={20} />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Product Grid/List - BOTONES COMPACTOS DEFINITIVOS */}
        {currentProducts.length > 0 ? (
          <div className={viewMode === 'grid' 
            ? "grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            : "space-y-4"
          }>
            <AnimatePresence>
              {currentProducts.map((product, index) => (
                <motion.div
                  key={product._id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  whileHover={{ 
                    y: -4,
                    boxShadow: "0 15px 30px -10px rgba(0, 0, 0, 0.1)"
                  }}
                  className={`bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:border-gray-200 transition-all ${
                    showDeleted ? 'border-l-4 border-l-red-500' : ''
                  } ${viewMode === 'list' ? 'flex min-h-32' : 'flex flex-col'}`}
                >
                  {/* Image Container - TAMAÑO FIJO */}
                  <div className={`relative ${
                    viewMode === 'list' 
                      ? 'w-32 md:w-40 flex-shrink-0' 
                      : 'h-48 flex-shrink-0'
                  } bg-gradient-to-br from-[#0C4B45]/5 to-[#83F4E9]/10`}>
                    {product.allImages.length > 0 ? (
                      <>
                        <img
                          src={product.allImages[0]}
                          alt={product.name}
                          className={`w-full h-full object-contain cursor-pointer ${
                            viewMode === 'list' ? 'p-4' : 'p-5'
                          }`}
                          onClick={() => openImageGallery(product.allImages, 0)}
                        />
                        {product.allImages.length > 1 && (
                          <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 shadow-lg">
                            <span className="text-xs font-semibold text-[#662D8F]">
                              +{product.allImages.length - 1}
                            </span>
                          </div>
                        )}
                        <div className="absolute top-3 right-3 flex flex-col gap-2">
                          <motion.button
                            whileHover={{ scale: 1.1, backgroundColor: "rgba(255,255,255,0.9)" }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openImageGallery(product.allImages, 0);
                            }}
                            className="bg-white/80 backdrop-blur-sm p-2 rounded-xl shadow-md"
                            title="Ver galería"
                          >
                            <FiZoomIn className="text-[#662D8F] w-3 h-3 md:w-4 md:h-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1, backgroundColor: "rgba(255,255,255,0.9)" }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openQuickView(product);
                            }}
                            className="bg-white/80 backdrop-blur-sm p-2 rounded-xl shadow-md"
                            title="Vista rápida"
                          >
                            <FiEye className="text-[#0C4B45] w-3 h-3 md:w-4 md:h-4" />
                          </motion.button>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <FiImage className="w-10 h-10 md:w-12 md:h-12" />
                      </div>
                    )}
                  </div>

                  {/* Product Info - CONTENIDO CONTROLADO */}
                  <div className={`flex-1 p-4 md:p-5 flex flex-col min-w-0 ${
                    viewMode === 'list' ? 'justify-between' : ''
                  }`}>
                    {/* Header con nombre y precio */}
                    <div className="flex justify-between items-start gap-2 mb-3">
                      <h3 
                        className="font-bold text-[#0C4B45] text-base leading-tight line-clamp-2 flex-1 min-w-0 break-words pr-2"
                        title={product.name}
                      >
                        {product.name}
                      </h3>
                      <div className="flex items-center text-[#662D8F] font-bold text-base whitespace-nowrap flex-shrink-0">
                        <FiDollarSign className="mr-1 flex-shrink-0" size={12} />
                        <span className="text-sm md:text-base">
                          {product.price?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>

                    {/* Descripción - ALTURA FIJA */}
                    <div className="mb-3 flex-1">
                      <p className="text-gray-600 text-xs leading-relaxed line-clamp-2 h-8 overflow-hidden">
                        {product.description || 'Sin descripción disponible'}
                      </p>
                    </div>

                    {/* Información adicional solo en vista lista */}
                    {viewMode === 'list' && (
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                          product.stock > 0 
                            ? 'bg-green-100 text-green-800 border border-green-200' 
                            : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          {product.stock > 0 ? `${product.stock} stock` : 'Agotado'}
                        </span>
                        {product.category && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold border border-blue-200 flex-shrink-0">
                            {product.category}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Footer con acciones - BOTONES SUPER COMPACTOS */}
                    <div className="flex justify-between items-center gap-2 pt-3 border-t border-gray-100">
                      {viewMode === 'grid' && (
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                          product.stock > 0 
                            ? 'bg-green-100 text-green-800 border border-green-200' 
                            : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          {product.stock > 0 ? `${product.stock} stock` : 'Agotado'}
                        </span>
                      )}
                      
                      <div className="flex gap-1 flex-shrink-0">
                        {showDeleted ? (
                          <>
                            {/* BOTONES COMPACTOS PARA PRODUCTOS ARCHIVADOS */}
                            <motion.button
                              whileHover={{ scale: 1.05, backgroundColor: "#22c55e" }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleRestoreProduct(product._id)}
                              className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all shadow flex items-center justify-center"
                              title="Restaurar producto"
                            >
                              <FiRotateCcw size={14} />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05, backgroundColor: "#ef4444" }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleHardDelete(product._id)}
                              className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all shadow flex items-center justify-center"
                              title="Eliminar permanentemente"
                            >
                              <FiTrash2 size={14} />
                            </motion.button>
                          </>
                        ) : (
                          <>
                            {/* BOTONES COMPACTOS PARA PRODUCTOS ACTIVOS */}
                            <motion.button
                              whileHover={{ scale: 1.05, backgroundColor: "#0C4B45" }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleEditProduct(product._id)}
                              className="p-2 bg-[#0C4B45] text-white rounded-lg hover:bg-[#083D38] transition-all shadow flex items-center justify-center"
                              title="Editar producto"
                            >
                              <FiEdit size={14} />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05, backgroundColor: "#f59e0b" }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleSoftDelete(product._id)}
                              className="p-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all shadow flex items-center justify-center"
                              title="Archivar producto"
                            >
                              <FiArchive size={14} />
                            </motion.button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-12 text-center border border-white/20"
          >
            <motion.div
              animate={{ 
                y: [0, -10, 0],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <FiPackage className="mx-auto text-gray-300 mb-6 w-20 h-20" />
            </motion.div>
            <h3 className="text-2xl font-bold text-[#0C4B45] mb-4">
              {showDeleted ? 'No hay productos archivados' : 'Catálogo Vacío'}
            </h3>
            <p className="text-gray-600 mb-8 text-lg max-w-md mx-auto leading-relaxed">
              {showDeleted 
                ? 'Los productos archivados aparecerán aquí. Actualmente no hay productos en el archivo.' 
                : 'Comienza agregando tu primer producto para construir tu catálogo.'}
            </p>
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 15px 30px -5px rgba(102, 45, 143, 0.4)" }}
              whileTap={{ scale: 0.95 }}
              onClick={showDeleted ? () => setShowDeleted(false) : handleAddProduct}
              className="px-8 py-4 bg-gradient-to-r from-[#662D8F] to-[#512577] text-white rounded-2xl hover:from-[#512577] hover:to-[#662D8F] transition-all shadow-lg font-semibold text-lg"
            >
              {showDeleted ? (
                <>
                  <FiRefreshCw className="inline mr-3" />
                  Ver Productos Activos
                </>
              ) : (
                <>
                  <FiPlus className="inline mr-3" />
                  Agregar Primer Producto
                </>
              )}
            </motion.button>
          </motion.div>
        )}

        {/* Paginación Mejorada */}
        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mt-8 border border-white/20"
          >
            <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
              <div className="text-center lg:text-left">
                <p className="text-sm text-gray-600 mb-1">Mostrando productos</p>
                <p className="text-lg font-semibold text-[#0C4B45]">
                  <span className="text-[#662D8F]">{startIndex + 1}-{endIndex}</span> de{' '}
                  <span className="text-[#0C4B45]">{totalItems}</span> total
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <motion.button
                  whileHover={{ scale: 1.05, backgroundColor: "#0C4B45" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={goToFirstPage}
                  disabled={currentPage === 1}
                  className="p-3 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed bg-gray-100 text-gray-600 hover:bg-[#0C4B45] hover:text-white transition-all shadow-md"
                  title="Primera página"
                >
                  <FiChevronsLeft size={18} />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05, backgroundColor: "#0C4B45" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className="p-3 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed bg-gray-100 text-gray-600 hover:bg-[#0C4B45] hover:text-white transition-all shadow-md"
                  title="Página anterior"
                >
                  <FiChevronLeft size={18} />
                </motion.button>

                <div className="flex space-x-1 mx-2">
                  {getPageNumbers().map(page => (
                    <motion.button
                      key={page}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => goToPage(page)}
                      className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all shadow-md ${
                        currentPage === page
                          ? 'bg-gradient-to-r from-[#0C4B45] to-[#083D38] text-white shadow-lg'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                      }`}
                    >
                      {page}
                    </motion.button>
                  ))}
                </div>

                <motion.button
                  whileHover={{ scale: 1.05, backgroundColor: "#0C4B45" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className="p-3 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed bg-gray-100 text-gray-600 hover:bg-[#0C4B45] hover:text-white transition-all shadow-md"
                  title="Página siguiente"
                >
                  <FiChevronRight size={18} />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05, backgroundColor: "#0C4B45" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={goToLastPage}
                  disabled={currentPage === totalPages}
                  className="p-3 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed bg-gray-100 text-gray-600 hover:bg-[#0C4B45] hover:text-white transition-all shadow-md"
                  title="Última página"
                >
                  <FiChevronsRight size={18} />
                </motion.button>
              </div>

              <div className="text-center lg:text-right">
                <p className="text-sm text-gray-600 mb-1">Página actual</p>
                <p className="text-lg font-semibold text-[#662D8F]">
                  {currentPage} de {totalPages}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Modal de Vista Rápida */}
      <AnimatePresence>
        {showQuickView && selectedProduct && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowQuickView(false)}
          >
            <motion.div
              className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2 bg-gradient-to-br from-[#0C4B45]/10 to-[#83F4E9]/10 p-6 flex items-center justify-center">
                  <img
                    src={selectedProduct.allImages[0]}
                    alt={selectedProduct.name}
                    className="max-h-64 object-contain"
                  />
                </div>
                <div className="md:w-1/2 p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-[#0C4B45]">{selectedProduct.name}</h3>
                    <button
                      onClick={() => setShowQuickView(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <FiX size={24} />
                    </button>
                  </div>
                  <p className="text-gray-600 mb-4">{selectedProduct.description}</p>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Precio:</span>
                      <span className="text-xl font-bold text-[#662D8F]">
                        ${selectedProduct.price?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Stock:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        selectedProduct.stock > 0 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedProduct.stock > 0 ? `${selectedProduct.stock} unidades` : 'Agotado'}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-3 mt-6">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleEditProduct(selectedProduct._id)}
                      className="flex-1 bg-[#0C4B45] text-white py-3 rounded-xl font-semibold shadow-md"
                    >
                      Editar Producto
                    </motion.button>
                    {!showDeleted && (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleSoftDelete(selectedProduct._id)}
                        className="px-6 bg-orange-500 text-white py-3 rounded-xl font-semibold shadow-md"
                      >
                        <FiArchive size={18} />
                      </motion.button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fullscreen Image Gallery */}
      <AnimatePresence>
        {showFullscreenImage && (
          <motion.div 
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowFullscreenImage(false)}
          >
            <motion.div 
              className="relative max-w-4xl md:max-w-6xl w-full max-h-[90vh]"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowFullscreenImage(false)}
                className="absolute top-4 right-4 text-white text-2xl z-10 hover:text-[#F2A9FD] bg-black/30 rounded-full p-2 backdrop-blur-sm"
              >
                <FiX />
              </button>
              
              <div className="relative h-full w-full flex items-center justify-center">
                <motion.img
                  key={selectedImageIndex}
                  src={currentProductImages[selectedImageIndex]}
                  alt="Producto"
                  className="max-h-full max-w-full object-contain"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
                
                {currentProductImages.length > 1 && (
                  <>
                    <motion.button 
                      whileHover={{ scale: 1.1, backgroundColor: "rgba(255,255,255,0.3)" }}
                      onClick={(e) => { e.stopPropagation(); prevImage(); }}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm p-3 rounded-full text-white hover:bg-white/30 transition-all"
                    >
                      <FiChevronLeft className="text-xl" />
                    </motion.button>
                    <motion.button 
                      whileHover={{ scale: 1.1, backgroundColor: "rgba(255,255,255,0.3)" }}
                      onClick={(e) => { e.stopPropagation(); nextImage(); }}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm p-3 rounded-full text-white hover:bg-white/30 transition-all"
                    >
                      <FiChevronRight className="text-xl" />
                    </motion.button>
                    
                    <div className="absolute bottom-6 left-0 right-0 flex justify-center space-x-2">
                      {currentProductImages.map((_, index) => (
                        <button 
                          key={index}
                          onClick={(e) => { e.stopPropagation(); setSelectedImageIndex(index); }}
                          className={`w-3 h-3 rounded-full transition-all ${
                            selectedImageIndex === index 
                              ? 'bg-white scale-125' 
                              : 'bg-white/50 hover:bg-white/70'
                          }`}
                        />
                      ))}
                    </div>
                    
                    <div className="absolute top-4 left-4 text-white/80 text-sm bg-black/30 backdrop-blur-sm px-3 py-1 rounded-full">
                      {selectedImageIndex + 1} / {currentProductImages.length}
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductList;
